import { UserModel } from "../types/user";

const USER_KEY = "user";
const TOKEN_KEY = "token";
const ROLE_KEY = "active_role";

const EMPTY_USER = { id: null } as unknown as UserModel;

/** Safely read + parse the stored session user. Never throws on corrupt storage. */
export const getStoredUser = (): UserModel => {
    try {
        const raw = localStorage.getItem(USER_KEY);
        if (!raw) return EMPTY_USER;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object"
            ? (parsed as UserModel)
            : EMPTY_USER;
    } catch {
        // Corrupt JSON in storage: treat as logged out instead of crashing the app.
        return EMPTY_USER;
    }
};

/** Access token from the stored session user, or null. */
export const getAccessToken = (): string | null => {
    const user = getStoredUser() as { accessToken?: string | null };
    return user?.accessToken ?? null;
};

/** Clear all session-related storage (logout / 401). */
export const clearStoredSession = (): void => {
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ROLE_KEY);
};

// Only the user-facing fields worth refreshing from an authoritative source (GET /user/me).
// Everything else (id, accessToken, etc.) is preserved from the stored session user — the
// stored object is JSON-parsed on every API request, so it must stay lean.
// roles/permissions (RBAC) precisam ser atuais a cada load — são a base do gate de UI.
const REFRESHABLE_FIELDS = [
    "name",
    "email",
    "avatar",
    "role",
    "roles",
    "permissions",
] as const;

export const mergeSessionUser = (
    stored: UserModel,
    fresh: Partial<UserModel> | null | undefined,
): UserModel => {
    if (!fresh) return stored;

    const picked: Partial<UserModel> = {};
    for (const key of REFRESHABLE_FIELDS) {
        if (fresh[key] !== undefined) {
            (picked as Record<string, unknown>)[key] = fresh[key];
        }
    }

    return { ...stored, ...picked };
};
